/* eslint-disable */
// @ts-nocheck
/**
 * GENERATED FILE — DO NOT EDIT.
 *
 * Verbatim copy of `server/lib/leadFormRules.js`, which is the single authored source.
 * Edit that file, then run `npm run sync:shared` from the repo root.
 *
 * It is copied rather than imported because CI builds this app with `docker build ./webapp`,
 * so `server/` is not in the build context. `webapp/scripts/check-shared-sync.mjs` fails the
 * build if this copy and the source ever differ; read it before changing this arrangement.
 *
 * Imported as `@shared/leadFormRules` (aliased in next.config.mjs), typed by
 * `src/types/sharedLeadFormRules.d.ts`.
 */

/**
 * Lead form rule evaluation — the ONE implementation.
 *
 * Imported verbatim by the server (validation, persistence), the public renderer, and
 * the admin builder's Test Run. There must never be a second copy: two evaluators that
 * disagree on an edge case produce a form the author tested green and users cannot
 * submit, and the divergence only ever shows up in production lead data.
 *
 * Deliberately DEPENDENCY-FREE and framework-free plain ESM — no zod, no lodash, no
 * Node built-ins — so the same file can be bundled into the browser and imported by
 * Express without either side pulling the other's toolchain in.
 *
 * The predicate AST is small on purpose (§4.2). Every extra operator is one the builder
 * UI must render, the save-time validator must statically analyse, and the client and
 * server must agree on down to type coercion. Everything the brief asks for fits in what
 * is here.
 *
 *   leaf:  { field: "cq1", op: "eq", value: "yes" }
 *   nodes: { all: [ … ] } | { any: [ … ] } | { not: <node> }
 */

/** Rule slots a field may carry. */
export const RULE_SLOTS = ["visibleWhen", "requiredWhen", "disabledWhen"];

export const RULE_OPS = [
  "eq",
  "ne",
  "in",
  "notIn",
  "gt",
  "lt",
  "isEmpty",
  "isNotEmpty",
];

/** Ops that ignore `value` entirely — the builder must not ask for one, and the
 *  save-time validator must not require one. */
export const UNARY_RULE_OPS = ["isEmpty", "isNotEmpty"];

/**
 * "Empty" for rule purposes. An unanswered field, a cleared text box and an empty
 * multi-select must all read the same way, otherwise `isEmpty` behaves differently
 * depending on which control produced the value.
 */
const isBlank = (v) => {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
};

/** Compare as strings — answers arrive as strings from form controls either way, and
 *  `"3" == 3` style coercion is the kind of edge case the two sides would drift on. */
const sameValue = (a, b) => String(a ?? "") === String(b ?? "");

/**
 * Numeric comparison for gt/lt. A non-numeric operand makes the predicate FALSE rather
 * than throwing: a text answer compared with `gt` is an authoring mistake, and a form
 * that silently hides a field is far less damaging than one that 500s on render.
 */
const numericCompare = (a, b, cmp) => {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return cmp(x, y);
};

const evaluateLeaf = (node, answers) => {
  const actual = answers?.[node.field];
  switch (node.op) {
    case "eq":
      return sameValue(actual, node.value);
    case "ne":
      return !sameValue(actual, node.value);
    case "in":
      return Array.isArray(node.value) && node.value.some((v) => sameValue(actual, v));
    case "notIn":
      return Array.isArray(node.value) && !node.value.some((v) => sameValue(actual, v));
    case "gt":
      return numericCompare(actual, node.value, (x, y) => x > y);
    case "lt":
      return numericCompare(actual, node.value, (x, y) => x < y);
    case "isEmpty":
      return isBlank(actual);
    case "isNotEmpty":
      return !isBlank(actual);
    default:
      // An unknown operator (a typo, or a config written by a newer build) must not
      // decide the form's shape. FALSE is the conservative answer: it hides an
      // optional field rather than revealing one the author never intended.
      return false;
  }
};

/**
 * Evaluate a predicate against a flat `{fieldId: value}` map.
 *
 * An ABSENT predicate is `true` — "no condition" means "always". This is what makes
 * `visibleWhen` optional on the overwhelming majority of fields.
 *
 * @param {object|null|undefined} node
 * @param {Record<string, unknown>} answers
 * @returns {boolean}
 */
export function evaluatePredicate(node, answers) {
  if (node == null) return true;
  if (typeof node !== "object" || Array.isArray(node)) return false;

  if (Array.isArray(node.all)) return node.all.every((n) => evaluatePredicate(n, answers));
  if (Array.isArray(node.any)) return node.any.some((n) => evaluatePredicate(n, answers));
  if (Object.prototype.hasOwnProperty.call(node, "not")) {
    return !evaluatePredicate(node.not, answers);
  }
  if (typeof node.field === "string" && typeof node.op === "string") {
    return evaluateLeaf(node, answers);
  }
  // A malformed node is not a condition anyone can satisfy.
  return false;
}

/**
 * A SPACER is layout, not data: it renders nothing, collects nothing and is never
 * validated or persisted.
 *
 * Duplicated from constants/leadForm.js ON PURPOSE — this module is dependency-free so it
 * can be bundled into the browser and imported by Express without either side dragging in
 * the other's toolchain, and a one-line type test is a smaller price than an import edge.
 * Keep the two in step; there is a test asserting they agree.
 */
const SPACER_TYPE = "spacer";
export const isSpacerField = (field) => field?.type === SPACER_TYPE;

/**
 * Every ANSWERABLE `fields` entry across every step, in journey order.
 *
 * OTP steps carry no fields, and spacers are skipped: including a spacer here would give
 * it a visibility state nothing reads, and — worse — make it eligible for the answer
 * pruning below, where a stray key matching its id would be treated as its answer.
 */
export function collectFields(steps) {
  const out = [];
  for (const step of Array.isArray(steps) ? steps : []) {
    for (const field of Array.isArray(step?.fields) ? step.fields : []) {
      if (field && typeof field.id === "string" && !isSpacerField(field)) {
        out.push({ field, step });
      }
    }
  }
  return out;
}

/** Spacers, with their resolved visibility — the renderer needs these, the validator does not. */
export function collectSpacers(steps) {
  const out = [];
  for (const step of Array.isArray(steps) ? steps : []) {
    for (const field of Array.isArray(step?.fields) ? step.fields : []) {
      if (isSpacerField(field)) out.push({ field, step });
    }
  }
  return out;
}

/** Every field id a predicate reads. Used by the save-time validator for cycle and
 *  forward-reference analysis, and exported because the builder highlights dependencies. */
export function collectPredicateFieldRefs(node, into = new Set()) {
  if (node == null || typeof node !== "object") return into;
  if (Array.isArray(node.all)) node.all.forEach((n) => collectPredicateFieldRefs(n, into));
  if (Array.isArray(node.any)) node.any.forEach((n) => collectPredicateFieldRefs(n, into));
  if (Object.prototype.hasOwnProperty.call(node, "not")) {
    collectPredicateFieldRefs(node.not, into);
  }
  if (typeof node.field === "string") into.add(node.field);
  return into;
}

/**
 * Why the settle loop always terminates, and how the bound is derived.
 *
 * Each pass can only DELETE answers (a hidden field's answer is dropped) — it never adds
 * one back. The answer set is therefore monotonically decreasing, so it cannot oscillate,
 * and a chain where each pass hides exactly one more field settles in at most one pass
 * per answerable field. The bound is `fieldCount + 2`: one pass per field, plus the pass
 * that confirms no change, plus one for luck.
 *
 * A FIXED bound (the old `50`) was a latent bug: a journey with more than that many
 * chained fields would exhaust the budget and return a half-settled state — silently
 * wrong, with a required field possibly still marked required after its parent vanished.
 * Deriving it from the schema makes the loop correct at any size, and the absolute
 * ceiling only exists so a pathological hand-edited config cannot hang a request.
 */
const ABSOLUTE_SETTLE_CEILING = 1000;
const settlePassBudget = (fieldCount) =>
  Math.min(fieldCount + 2, ABSOLUTE_SETTLE_CEILING);

/**
 * Resolve which fields are visible / required / disabled, and prune the answers of
 * fields that are not visible — iterated to a FIXED POINT.
 *
 * The iteration is the whole point. Hiding a parent can hide a grandchild, and dropping
 * the parent's answer can in turn change a predicate that read it. A single pass leaves
 * a revealed-then-hidden branch's stale answer in the payload, which is how a value the
 * user never intended reaches the CRM.
 *
 * Two invariants are enforced here rather than left to callers, because every caller
 * would otherwise have to remember them:
 *
 *   1. HIDDEN ⇒ NOT REQUIRED. A hidden-but-required field makes submit impossible with
 *      nothing on screen to explain why.
 *   2. HIDDEN ⇒ VALUE DROPPED.
 *
 * @param {Array} steps  journey steps (all of them, or the prefix up to the current one)
 * @param {Record<string, unknown>} answers
 * @returns {{
 *   visible: Set<string>, required: Set<string>, disabled: Set<string>,
 *   answers: Record<string, unknown>, settled: boolean
 * }} `answers` is a pruned COPY; the input is never mutated.
 */
export function resolveFieldStates(steps, answers = {}) {
  const entries = collectFields(steps);
  let current = { ...answers };
  let settled = false;

  let visible = new Set();
  let required = new Set();
  let disabled = new Set();

  const budget = settlePassBudget(entries.length);
  for (let pass = 0; pass < budget; pass += 1) {
    visible = new Set();
    required = new Set();
    disabled = new Set();

    for (const { field } of entries) {
      const rules = field.rules ?? {};
      if (!evaluatePredicate(rules.visibleWhen, current)) continue;
      visible.add(field.id);

      // `required: true` is the static default; `requiredWhen` makes it conditional.
      // Both are only consulted for a VISIBLE field — invariant 1.
      const isRequired = Object.prototype.hasOwnProperty.call(rules, "requiredWhen")
        ? evaluatePredicate(rules.requiredWhen, current)
        : field.required === true;
      if (isRequired) required.add(field.id);

      if (rules.disabledWhen != null && evaluatePredicate(rules.disabledWhen, current)) {
        disabled.add(field.id);
      }
    }

    // Invariant 2 — drop answers belonging to fields that are no longer visible.
    const next = {};
    for (const [key, value] of Object.entries(current)) {
      const owned = entries.some(({ field }) => field.id === key);
      // Keys that belong to no field at all (mobile carried alongside, say) are left
      // untouched: this function owns the schema's fields, not the whole payload.
      if (!owned || visible.has(key)) next[key] = value;
    }

    const unchanged =
      Object.keys(next).length === Object.keys(current).length &&
      Object.keys(next).every((k) => next[k] === current[k]);
    current = next;
    if (unchanged) {
      settled = true;
      break;
    }
  }

  return { visible, required, disabled, answers: current, settled };
}

/**
 * The fields a given step should render right now, in author order.
 *
 * Visibility is resolved against the WHOLE journey's answers, not just this step's, so a
 * rule may depend on any earlier step's answer (R21).
 */
export function visibleFieldsForStep(steps, stepId, answers = {}) {
  const { visible } = resolveFieldStates(steps, answers);
  const step = (Array.isArray(steps) ? steps : []).find((s) => s?.id === stepId);
  if (!step || !Array.isArray(step.fields)) return [];
  return step.fields.filter((f) => {
    if (!f) return false;
    // Spacers RENDER (that is their whole job) but have no resolved state, so they are
    // gated on their own visibleWhen directly — letting an author hide a gap along with
    // the branch it was separating.
    if (isSpacerField(f)) return evaluatePredicate(f.rules?.visibleWhen, answers);
    return visible.has(f.id);
  });
}


/* ─── Where the lead is submitted ───────────────────────────────────────────────────
 *
 * A journey drops the lead to the CRM at exactly ONE step, chosen by the author with
 * `submitsLead` on that step. Everything before it only persists answers to our own row;
 * everything after it updates a lead the CRM already has.
 *
 * Why it is a choice at all: the useful moment differs per form. A short form wants the
 * lead the instant the number is verified, so sales can call while the visitor is still on
 * the page. A longer one wants the enrichment questions included in the first record, so
 * the CRM never sees a half-empty lead. Hardcoding either is wrong for the other.
 */

/** The OTP marker. Duplicated rather than imported — this file has no imports by design. */
const OTP_STEP_KIND = "otp";

/**
 * The index of the LAST OTP gate, or -1 when the journey has none.
 *
 * The last, not the first: a journey may verify more than one thing, and submitting before
 * a later gate would drop a lead whose remaining claims are still unproven.
 */
export function lastOtpStepIndex(steps) {
  const list = Array.isArray(steps) ? steps : [];
  let last = -1;
  for (let i = 0; i < list.length; i += 1) {
    if (list[i]?.kind === OTP_STEP_KIND) last = i;
  }
  return last;
}

/**
 * Which step drops the lead to the CRM.
 *
 * An explicit `submitsLead` wins. With none set, the answer is the LAST step — which is
 * exactly where submission has always happened, so every schema authored before this
 * existed keeps behaving identically without being rewritten. New schemas are required to
 * be explicit (see services/leadFormSchema.js), and the builder marks the last eligible
 * step for an author opening an older form, so the fallback fades out as forms are edited
 * rather than needing a migration.
 *
 * Returns -1 for an empty journey — there is nothing to submit.
 */
export function resolveSubmitStepIndex(steps) {
  const list = Array.isArray(steps) ? steps : [];
  if (list.length === 0) return -1;
  const explicit = list.findIndex((s) => s?.submitsLead === true);
  return explicit === -1 ? list.length - 1 : explicit;
}

/**
 * Every step index that may legally carry the submit flag: the last OTP gate and anything
 * after it. A journey with no gate has no such constraint — it has nothing to wait for —
 * but it also delivers an UNVERIFIED lead, which the builder says out loud.
 */
export function eligibleSubmitStepIndexes(steps) {
  const list = Array.isArray(steps) ? steps : [];
  const from = Math.max(0, lastOtpStepIndex(list));
  return list.map((_, i) => i).filter((i) => i >= from);
}
